import { useState } from "react";
import { APP_NAMES } from "@constants/Enums";
import {
  KeycloakGroupMappings,
  keycloakSubGroupMappings,
} from "@constants/KeycloakSettings";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/16/solid";

type Group = { id: string; name: string };
type TenantGroups = Record<string, Group[]>;

const GROUP_LABELS: Record<string, string> = {
  kakely_groups: "KAKELY",
  yobely_groups: "YOBELY",
};

const PARENT_TO_KEY: Record<string, keyof typeof GROUP_LABELS> = {
  global: "global_groups",
  settings: "settings_groups",
  data: "data_groups",
  kakely: "kakely_groups",
  yobely: "yobely_groups",
};

export const UserKeycloakGroupInfoCard = ({
  tenantKeycloakGroups,
  userKeycloakGroups,
  hasKakelyLicense,
  hasYobelyLicense,
  handleCheckboxChange,
  error_message,
}: {
  tenantKeycloakGroups: TenantGroups;
  userKeycloakGroups: Group[];
  hasKakelyLicense: false;
  hasYobelyLicense: false;
  handleCheckboxChange: (group: Group) => void;
  error_message: any;
}) => {
  const groupedEntries = Object.entries(tenantKeycloakGroups ?? {})
    .filter(
      ([key, arr]) =>
        key.endsWith("_groups") &&
        !key.startsWith("tenant") &&
        Array.isArray(arr) &&
        arr.length > 0,
    )
    .sort(([a], [b]) => {
      const ai = Object.keys(GROUP_LABELS).indexOf(a);
      const bi = Object.keys(GROUP_LABELS).indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

  const [expandedItems, setExpandedItems] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleItem = (key: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const labelForParent = (parent?: string) => {
    if (!parent) return undefined;
    const key = PARENT_TO_KEY[parent.toLowerCase()];
    return key ? GROUP_LABELS[key] : undefined;
  };

  const hasAnyGroups = groupedEntries.length > 0;

  const isChecked = (groupId: string) =>
    Array.isArray(userKeycloakGroups) &&
    userKeycloakGroups.some((g) => g.id === groupId);

  const checkSubGroups = (groups, parent) => {
    const subgroups: Record<string, any[]> = {};
    let subGroupLabel = "";
    groups.forEach((group) => {
      const keyIndex = labelForParent(group.parent.toUpperCase());
      if (!keyIndex) {
        if (!subgroups[group.parent]) {
          subgroups[group.parent] = [];
        }
        if (group.parent.toUpperCase() !== parent.toUpperCase()) {
          subGroupLabel = group.parent;
          subgroups[group.parent].push(group);
        }
      }
    });

    return subgroups;
  };

  const dropSubGroups = (subgroups: Record<string, any[]>, groups: any[]) => {
    const subGroupLabels = Object.keys(subgroups);

    return groups.filter(
      (group) =>
        !subGroupLabels.includes(group.parent) &&
        !subGroupLabels.includes(group.name),
    );
  };

  return (
    <div>
      <h3 className="mt-10 mb-5 label-light-blue-xl">機能制限グループ</h3>

      {!hasAnyGroups || !hasKakelyLicense ? (
        <div className="text-xl">{error_message?.user?.no_groups}</div>
      ) : (
        <div className="flex flex-col gap-8">
          {groupedEntries.map(([key, groups]) => {
            const rawLabel = key.replace(/_groups$/, "");
            const label = GROUP_LABELS[key] ?? rawLabel;
            const sorted = [...groups].sort((a, b) =>
              a.name.localeCompare(b.name),
            );

            const seen = new Set<string>();
            const unique = sorted.filter((g) =>
              seen.has(g.id) ? false : (seen.add(g.id), true),
            );

            if (label.toUpperCase() === APP_NAMES.YOBELY && !hasYobelyLicense)
              return null;
            if (label.toUpperCase() === APP_NAMES.KAKELY && !hasKakelyLicense)
              return null;

            const subgroups = checkSubGroups(groups, label);

            const new_groups = dropSubGroups(subgroups, unique);

            return (
              <section key={label}>
                <h4
                  className="flex items-center gap-2 text-lg font-semibold text-gray-700"
                  onClick={() => toggleItem(key)}
                >
                  {label}{" "}
                  {expandedItems[key] ? (
                    <ChevronUpIcon className="w-6 h-6" />
                  ) : (
                    <ChevronDownIcon className="w-6 h-6" />
                  )}
                </h4>
                <div className="text-black flex flex-wrap gap-4 mt-2">
                  {expandedItems[key] &&
                    new_groups.map((group) => (
                      <label key={group.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-blue-600"
                          checked={!isChecked(group.id)}
                          onChange={() => handleCheckboxChange(group)}
                        />
                        <span className="text-base">
                          {KeycloakGroupMappings[
                            group.name.split("/")[
                              group.name.split("/").length - 1
                            ]
                          ] || "未定義の機能"}
                        </span>
                      </label>
                    ))}
                </div>
                {expandedItems[key] && Object.keys(subgroups).length > 0 && (
                  <div className="ml-4 mt-4 text-black gap-5">
                    {Object.keys(subgroups).map((parent) => (
                      <div key={parent}>
                        <h5
                          className="mt-2 font-bold text-md flex"
                          onClick={() => toggleItem(parent)}
                        >
                          {" "}
                          {keycloakSubGroupMappings[parent] ||
                            "未定義のサブグループ"}{" "}
                          {expandedItems[parent] ? (
                            <ChevronUpIcon className="w-6 h-6" />
                          ) : (
                            <ChevronDownIcon className="w-6 h-6" />
                          )}{" "}
                        </h5>
                        <div className="flex mt-2 flex-wrap gap-5">
                          {expandedItems[parent] &&
                            subgroups[parent].map((sub) => (
                              <label
                                key={sub.id}
                                className="flex items-center gap-2"
                              >
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 accent-blue-600"
                                  checked={!isChecked(sub.id)}
                                  onChange={() => handleCheckboxChange(sub)}
                                />
                                <span className="text-base">
                                  {KeycloakGroupMappings[
                                    sub.name.split("/")[
                                      sub.name.split("/").length - 1
                                    ]
                                  ] || "未定義の機能"}
                                </span>
                              </label>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <div className="mt-5 divider" />
    </div>
  );
};
